import type { ButtonHTMLAttributes, ReactNode } from 'react';

const classes = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

// ---- Button ---------------------------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'default' | 'large';
  block?: boolean;
  /** Shows the busy label and disables the control, so a form cannot be submitted twice. */
  busy?: boolean;
  busyLabel?: string;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary', size = 'default', block, busy, busyLabel, icon, children, className,
  disabled, type = 'button', ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classes(
        'dg-button',
        variant !== 'primary' && `dg-button--${variant}`,
        size === 'large' && 'dg-button--large',
        block && 'dg-button--block',
        className,
      )}
      disabled={disabled || busy}
      // Announces the state change to a screen reader without the label visibly jumping about.
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="dg-spinner__mark" aria-hidden="true" /> : icon}
      <span>{busy && busyLabel ? busyLabel : children}</span>
    </button>
  );
}

// ---- Card -----------------------------------------------------------------------------------------------

export interface CardProps {
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
}

export function Card({ title, hint, action, flush, className, children }: CardProps) {
  return (
    <section className={classes('dg-card', flush && 'dg-card--flush', className)}>
      {(title || action) && (
        <header className="dg-card__header">
          <div>
            {title && <h2 className="dg-card__title">{title}</h2>}
            {hint && <p className="dg-card__hint">{hint}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

// ---- Badge ----------------------------------------------------------------------------------------------

export type BadgeTone = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'count';

export function Badge({ tone = 'brand', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={classes('dg-badge', tone !== 'brand' && `dg-badge--${tone}`)}>{children}</span>;
}

// ---- Spinner --------------------------------------------------------------------------------------------

/**
 * A busy indicator that says so out loud.
 *
 * The label is not decorative: without it, a screen-reader user is told nothing at all while a slow
 * request runs on a 3G connection, which is the connection this product is built for (SRS §2.3).
 */
export function Spinner({ label }: { label: string }) {
  return (
    <span className="dg-spinner" role="status">
      <span className="dg-spinner__mark" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

// ---- Notice ---------------------------------------------------------------------------------------------

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

export interface NoticeProps {
  tone?: NoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  /** A trace reference from the API, shown small and in Latin script so it can be read out to support. */
  reference?: string | null;
  icon?: ReactNode;
}

export function Notice({ tone = 'info', title, children, reference, icon }: NoticeProps) {
  return (
    <div
      className={classes('dg-notice', tone !== 'info' && `dg-notice--${tone}`)}
      // Only a problem interrupts; an informational notice waits its turn.
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <div className="dg-stack dg-stack--tight">
        {title && <p className="dg-notice__title">{title}</p>}
        {children && <div className="dg-notice__body">{children}</div>}
        {reference && <p className="dg-notice__reference">{reference}</p>}
      </div>
    </div>
  );
}

// ---- Statistic tile -------------------------------------------------------------------------------------

export function Stat({ label, value, note, tone }: {
  label: ReactNode; value: ReactNode; note?: ReactNode; tone?: BadgeTone;
}) {
  return (
    <div className="dg-stat">
      <span className="dg-stat__label">{label}</span>
      <span className="dg-stat__value">{value}</span>
      {note && (tone ? <Badge tone={tone}>{note}</Badge> : <span className="dg-stat__note">{note}</span>)}
    </div>
  );
}

// ---- Layout helpers -------------------------------------------------------------------------------------

export function Stack({ tight, className, children }: {
  tight?: boolean; className?: string; children: ReactNode;
}) {
  return <div className={classes('dg-stack', tight && 'dg-stack--tight', className)}>{children}</div>;
}

export function Row({ between, className, children }: {
  between?: boolean; className?: string; children: ReactNode;
}) {
  return <div className={classes('dg-row', between && 'dg-row--between', className)}>{children}</div>;
}

export function Grid({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={classes('dg-grid', className)}>{children}</div>;
}

export function PageHeader({ title, lede, actions }: {
  title: ReactNode; lede?: ReactNode; actions?: ReactNode;
}) {
  return (
    <header className="dg-page-header">
      <div className="dg-page-header__row">
        <h1>{title}</h1>
        {actions}
      </div>
      {lede && <p className="dg-page-header__lede">{lede}</p>}
    </header>
  );
}
